<template>
  <v-container fluid>

    <!-- Header -->
    <div class="d-flex align-center mb-4" style="gap:12px">
      <v-btn small outlined color="info" tile @click="$router.back()">
        <v-icon left>mdi-arrow-left-bold</v-icon>Volver
      </v-btn>
      <div>
        <div class="text-h6 font-weight-bold">Bandeja de Arrastre</div>
        <div class="caption grey--text">Programas en Arrastre o Arrastre Crítico que requieren decisión</div>
      </div>
      <v-spacer />
      <v-btn small outlined :loading="loadingArrastre || loadingCritico" @click="cargarTodo">
        <v-icon small left>mdi-refresh</v-icon> Actualizar
      </v-btn>
    </div>

    <!-- Tabs -->
    <v-tabs v-model="tabActivo" color="orange darken-2" background-color="transparent" class="mb-4">
      <v-tab>
        <v-icon left small>mdi-alert</v-icon>
        En Arrastre
        <v-chip x-small color="yellow darken-3" dark class="ml-2 font-weight-bold">{{ totalArrastre }}</v-chip>
      </v-tab>
      <v-tab>
        <v-icon left small>mdi-alert-octagon</v-icon>
        Arrastre Crítico
        <v-chip x-small color="deep-orange darken-2" dark class="ml-2 font-weight-bold">{{ totalCritico }}</v-chip>
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tabActivo">

      <!-- Tab 1: EN ARRASTRE -->
      <v-tab-item>
        <v-card outlined>
          <v-progress-linear v-if="loadingArrastre" indeterminate color="yellow darken-3" />
          <v-data-table
            :headers="headers"
            :items="programasArrastre"
            :loading="loadingArrastre"
            :footer-props="{ itemsPerPageOptions: [20, 50, 100] }"
            dense
            sort-by="dias_para_proximo_cambio"
            :sort-desc="false"
          >
            <template v-slot:item.programa="{ item }">
              <div>
                <v-chip x-small color="yellow darken-3" dark label class="mb-1 font-weight-bold">{{ item.codigo || '—' }}</v-chip>
                <div class="caption" style="max-width:220px;white-space:normal;line-height:1.3">{{ item.nombre_compuesto }}</div>
              </div>
            </template>

            <template v-slot:item.sede_nombre="{ item }">
              <div class="caption">{{ item.sede_nombre || '—' }}</div>
            </template>

            <template v-slot:item.categoria="{ item }">
              <v-chip x-small :color="categoriaColor(item.categoria)" dark label>{{ item.categoria || '—' }}</v-chip>
            </template>

            <template v-slot:item.inscritos="{ item }">
              <div class="d-flex align-center" style="gap:4px">
                <span class="body-2 font-weight-bold" :class="item.inscritos_efectivos >= puntoEquilibrio(item) ? 'green--text text--darken-2' : 'red--text text--darken-1'">
                  {{ item.inscritos_efectivos }}
                </span>
                <span class="caption grey--text">/ {{ puntoEquilibrio(item) }}</span>
                <v-icon v-if="item.es_estrella" x-small color="amber darken-2" title="Programa estrella">mdi-star</v-icon>
              </div>
            </template>

            <template v-slot:item.dias_para_proximo_cambio="{ item }">
              <v-chip x-small dark :color="colorUrgencia(item, 'ARRASTRE')">
                <v-icon left x-small>mdi-clock-alert-outline</v-icon>
                {{ labelUrgencia(item) }}
              </v-chip>
            </template>

            <template v-slot:item.fecha_matriculacion="{ item }">
              <div class="caption">{{ item.fecha_matriculacion || '—' }}</div>
              <div class="caption grey--text">{{ item.dias_desde_matriculacion }}d atrás</div>
            </template>

            <template v-slot:item.acciones="{ item }">
              <v-btn v-if="esMaster" small color="yellow darken-3" dark @click="abrirDecision(item, 'ARRASTRE')">
                <v-icon left small>mdi-gavel</v-icon> Decidir
              </v-btn>
              <span v-else class="caption grey--text">Sin permiso</span>
            </template>

            <template v-slot:no-data>
              <div class="text-center grey--text py-6">
                <v-icon large color="grey lighten-2">mdi-check-circle-outline</v-icon>
                <div class="mt-2 caption">No hay programas en Arrastre</div>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <!-- Tab 2: ARRASTRE CRÍTICO -->
      <v-tab-item>
        <v-card outlined>
          <v-progress-linear v-if="loadingCritico" indeterminate color="deep-orange darken-2" />
          <v-data-table
            :headers="headers"
            :items="programasCritico"
            :loading="loadingCritico"
            :footer-props="{ itemsPerPageOptions: [20, 50, 100] }"
            dense
            sort-by="dias_para_proximo_cambio"
            :sort-desc="false"
          >
            <template v-slot:item.programa="{ item }">
              <div>
                <v-chip x-small color="deep-orange darken-2" dark label class="mb-1 font-weight-bold">{{ item.codigo || '—' }}</v-chip>
                <div class="caption" style="max-width:220px;white-space:normal;line-height:1.3">{{ item.nombre_compuesto }}</div>
              </div>
            </template>

            <template v-slot:item.sede_nombre="{ item }">
              <div class="caption">{{ item.sede_nombre || '—' }}</div>
            </template>

            <template v-slot:item.categoria="{ item }">
              <v-chip x-small :color="categoriaColor(item.categoria)" dark label>{{ item.categoria || '—' }}</v-chip>
            </template>

            <template v-slot:item.inscritos="{ item }">
              <div class="d-flex align-center" style="gap:4px">
                <span class="body-2 font-weight-bold" :class="item.inscritos_efectivos >= puntoEquilibrio(item) ? 'green--text text--darken-2' : 'red--text text--darken-1'">
                  {{ item.inscritos_efectivos }}
                </span>
                <span class="caption grey--text">/ {{ puntoEquilibrio(item) }}</span>
                <v-icon v-if="item.es_estrella" x-small color="amber darken-2" title="Programa estrella">mdi-star</v-icon>
              </div>
            </template>

            <template v-slot:item.dias_para_proximo_cambio="{ item }">
              <v-chip x-small dark :color="colorUrgencia(item, 'ARRASTRE_CRITICO')">
                <v-icon left x-small>mdi-clock-alert-outline</v-icon>
                {{ labelUrgencia(item) }}
              </v-chip>
            </template>

            <template v-slot:item.fecha_matriculacion="{ item }">
              <div class="caption">{{ item.fecha_matriculacion || '—' }}</div>
              <div class="caption grey--text">{{ item.dias_desde_matriculacion }}d atrás</div>
            </template>

            <template v-slot:item.acciones="{ item }">
              <v-btn v-if="esMaster" small color="deep-orange darken-2" dark @click="abrirDecision(item, 'ARRASTRE_CRITICO')">
                <v-icon left small>mdi-gavel</v-icon> Decidir
              </v-btn>
              <span v-else class="caption grey--text">Sin permiso</span>
            </template>

            <template v-slot:no-data>
              <div class="text-center grey--text py-6">
                <v-icon large color="grey lighten-2">mdi-check-circle-outline</v-icon>
                <div class="mt-2 caption">No hay programas en Arrastre Crítico</div>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

    </v-tabs-items>

    <!-- DIALOG DECISIÓN -->
    <v-dialog v-model="dialogDecision" max-width="560" persistent>
      <v-card v-if="programaSeleccionado">
        <v-toolbar
          :color="estadoActual === 'ARRASTRE_CRITICO' ? 'deep-orange darken-2' : 'yellow darken-3'"
          dark dense flat
        >
          <v-icon class="mr-2">mdi-gavel</v-icon>
          <v-toolbar-title class="font-weight-bold">
            {{ estadoActual === 'ARRASTRE_CRITICO' ? 'Decisión — Arrastre Crítico' : 'Decisión — Arrastre' }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon :disabled="loadingDecision" @click="dialogDecision = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <!-- Info del programa -->
          <div class="pa-3 rounded mb-4" style="background:#f5f5f5; border-left:4px solid #546e7a">
            <div class="body-2 font-weight-bold">{{ programaSeleccionado.nombre_compuesto }}</div>
            <div class="caption grey--text mt-1">
              {{ programaSeleccionado.codigo }} · {{ programaSeleccionado.sede_nombre }} · {{ programaSeleccionado.categoria }}
            </div>
          </div>

          <!-- Indicador inscritos vs PE -->
          <div class="d-flex align-center justify-center mb-4 pa-3 rounded"
            :style="{ background: programaSeleccionado.inscritos_efectivos >= puntoEquilibrio(programaSeleccionado) ? '#e8f5e9' : '#fce4ec' }">
            <div class="text-center">
              <div class="caption grey--text mb-1">Inscritos efectivos / Punto de equilibrio</div>
              <div class="d-flex align-center justify-center" style="gap:8px">
                <span class="text-h5 font-weight-black"
                  :class="programaSeleccionado.inscritos_efectivos >= puntoEquilibrio(programaSeleccionado) ? 'green--text text--darken-2' : 'red--text text--darken-1'">
                  {{ programaSeleccionado.inscritos_efectivos }}
                </span>
                <span class="body-1 grey--text">/</span>
                <span class="body-1 font-weight-bold grey--text text--darken-2">{{ puntoEquilibrio(programaSeleccionado) }}</span>
                <v-icon v-if="programaSeleccionado.es_estrella" color="amber darken-2" small>mdi-star</v-icon>
              </div>
              <v-chip x-small class="mt-1"
                :color="programaSeleccionado.inscritos_efectivos >= puntoEquilibrio(programaSeleccionado) ? 'green darken-1' : 'red darken-1'"
                dark
              >
                {{ programaSeleccionado.inscritos_efectivos >= puntoEquilibrio(programaSeleccionado) ? 'Alcanzó el punto de equilibrio' : 'No alcanzó el punto de equilibrio' }}
              </v-chip>
            </div>
          </div>

          <!-- Opciones de decisión -->
          <div class="subtitle-2 mb-2">Selecciona una decisión:</div>
          <v-radio-group v-model="form.decision" class="mt-0">

            <!-- INICIAR MÓDULO 0 -->
            <v-radio
              value="INICIAR_MODULO_0"
              :disabled="programaSeleccionado.inscritos_efectivos < 10"
            >
              <template v-slot:label>
                <div>
                  <div class="body-2 font-weight-medium green--text text--darken-2">
                    <v-icon left small color="green darken-2">mdi-play-circle-outline</v-icon>
                    Iniciar Módulo 0
                  </div>
                  <div class="caption grey--text" v-if="programaSeleccionado.inscritos_efectivos < 10">
                    Requiere mínimo 10 inscritos efectivos (actualmente {{ programaSeleccionado.inscritos_efectivos }})
                  </div>
                  <div class="caption grey--text" v-else-if="programaSeleccionado.inscritos_efectivos < puntoEquilibrio(programaSeleccionado)">
                    Inicio excepcional — no alcanzó el punto de equilibrio ({{ programaSeleccionado.inscritos_efectivos }} de {{ puntoEquilibrio(programaSeleccionado) }})
                  </div>
                </div>
              </template>
            </v-radio>

            <!-- BAJA -->
            <v-radio value="BAJA" color="red darken-1">
              <template v-slot:label>
                <div>
                  <div class="body-2 font-weight-medium red--text text--darken-1">
                    <v-icon left small color="red darken-1">mdi-close-circle-outline</v-icon>
                    Dar de baja
                  </div>
                  <div class="caption red--text text--lighten-1">
                    Retira automáticamente a los inscritos activos del programa
                  </div>
                </div>
              </template>
            </v-radio>

            <!-- DESARROLLO SIN PE (solo Arrastre Crítico) -->
            <v-radio v-if="estadoActual === 'ARRASTRE_CRITICO'" value="DESARROLLO_SIN_PE" color="blue darken-2">
              <template v-slot:label>
                <div class="body-2 font-weight-medium blue--text text--darken-2">
                  <v-icon left small color="blue darken-2">mdi-play-speed</v-icon>
                  Desarrollo sin punto de equilibrio
                </div>
              </template>
            </v-radio>

          </v-radio-group>

          <!-- Campo fecha_inicio (solo para INICIAR_MODULO_0) -->
          <v-text-field
            v-if="form.decision === 'INICIAR_MODULO_0'"
            v-model="form.fecha_inicio"
            type="date"
            label="Fecha de inicio del Módulo 0"
            outlined dense
            :rules="[v => !!v || 'La fecha es obligatoria para iniciar Módulo 0']"
            class="mt-2"
          />

          <!-- Advertencia BAJA -->
          <v-alert
            v-if="form.decision === 'BAJA'"
            type="warning" dense outlined class="mt-2 mb-0"
            icon="mdi-alert-circle"
          >
            Al dar de baja, todas las inscripciones activas del programa pasarán a <strong>RETIRADO</strong> automáticamente.
          </v-alert>

          <!-- Descripción / motivo -->
          <v-textarea
            v-if="form.decision"
            v-model="form.descripcion"
            :label="form.decision === 'BAJA' ? 'Motivo de la baja' : 'Observación (opcional)'"
            outlined dense rows="2" auto-grow class="mt-3"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="loadingDecision" @click="dialogDecision = false">Cancelar</v-btn>
          <v-btn
            :color="form.decision === 'BAJA' ? 'red darken-1' : form.decision === 'DESARROLLO_SIN_PE' ? 'blue darken-2' : 'green darken-1'"
            dark
            :loading="loadingDecision"
            :disabled="!form.decision || (form.decision === 'INICIAR_MODULO_0' && !form.fecha_inicio)"
            @click="confirmarDecision"
          >
            <v-icon left small>mdi-check</v-icon> Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- SNACKBAR -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right :timeout="4000">
      {{ snackbar.msg }}
      <template v-slot:action>
        <v-btn text small @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>

  </v-container>
</template>

<script>
import xaxios from '@/xaxios';

const API_URL = `${process.env.VUE_APP_API_REST}/api/v1`;

export default {
  name: 'BandejaArrastre',

  data() {
    return {
      tabActivo: 0,
      programasArrastre: [],
      totalArrastre: 0,
      loadingArrastre: false,
      programasCritico: [],
      totalCritico: 0,
      loadingCritico: false,
      headers: [
        { text: 'Programa', value: 'programa', sortable: false, width: '230px' },
        { text: 'Sede', value: 'sede_nombre', sortable: false, width: '130px' },
        { text: 'Categoría', value: 'categoria', sortable: false, width: '110px' },
        { text: 'Inscritos / PE', value: 'inscritos', sortable: false, width: '110px' },
        { text: 'Urgencia', value: 'dias_para_proximo_cambio', sortable: true, width: '100px' },
        { text: 'Matrícula', value: 'fecha_matriculacion', sortable: false, width: '110px' },
        { text: 'Decisión', value: 'acciones', sortable: false, width: '120px' },
      ],
      dialogDecision: false,
      programaSeleccionado: null,
      estadoActual: null,
      form: {
        decision: null,
        fecha_inicio: null,
        descripcion: '',
      },
      loadingDecision: false,
      snackbar: { show: false, msg: '', color: 'success' },
    };
  },

  computed: {
    esMaster() {
      return this.$store.state.CARGO && this.$store.state.CARGO.id === 1;
    },
  },

  mounted() {
    this.cargarTodo();
  },

  methods: {
    cargarTodo() {
      this.fetchArrastre();
      this.fetchCritico();
    },

    async fetchArrastre() {
      this.loadingArrastre = true;
      try {
        const { data } = await xaxios.get(`${API_URL}/programas`, {
          params: { estado_programa_codigo: 'ARRASTRE', per_page: 100, page: 1, order: 'ASC' },
        });
        this.programasArrastre = data?.data || [];
        this.totalArrastre = data?.total || this.programasArrastre.length;
      } catch (e) {
        window.console.error('Error cargando arrastre:', e);
        this.notificar('Error al cargar programas en Arrastre', 'error');
      } finally {
        this.loadingArrastre = false;
      }
    },

    async fetchCritico() {
      this.loadingCritico = true;
      try {
        const { data } = await xaxios.get(`${API_URL}/programas`, {
          params: { estado_programa_codigo: 'ARRASTRE_CRITICO', per_page: 100, page: 1, order: 'ASC' },
        });
        this.programasCritico = data?.data || [];
        this.totalCritico = data?.total || this.programasCritico.length;
      } catch (e) {
        window.console.error('Error cargando arrastre crítico:', e);
        this.notificar('Error al cargar programas en Arrastre Crítico', 'error');
      } finally {
        this.loadingCritico = false;
      }
    },

    puntoEquilibrio(programa) {
      return programa.es_estrella ? 25 : 20;
    },

    colorUrgencia(item, tipo) {
      const d = item.dias_para_proximo_cambio;
      if (d <= 0) return 'red darken-2';
      if (d <= 3) return 'orange darken-2';
      return tipo === 'ARRASTRE_CRITICO' ? 'deep-orange darken-1' : 'yellow darken-3';
    },

    labelUrgencia(item) {
      const d = item.dias_para_proximo_cambio;
      return d <= 0 ? 'Vencido' : d + 'd';
    },

    categoriaColor(cat) {
      const map = { MAESTRIA: 'purple darken-1', ESPECIALIDAD: 'blue darken-1', DIPLOMADO: 'teal darken-1' };
      return map[cat] || 'grey';
    },

    abrirDecision(programa, estado) {
      this.programaSeleccionado = programa;
      this.estadoActual = estado;
      this.form = { decision: null, fecha_inicio: null, descripcion: '' };
      this.dialogDecision = true;
    },

    async confirmarDecision() {
      if (!this.form.decision) return;
      if (this.form.decision === 'INICIAR_MODULO_0' && !this.form.fecha_inicio) {
        this.notificar('La fecha de inicio es obligatoria para Iniciar Módulo 0', 'warning');
        return;
      }
      this.loadingDecision = true;
      const endpoint = this.estadoActual === 'ARRASTRE_CRITICO'
        ? `${API_URL}/programa/${this.programaSeleccionado.id}/decision-arrastre-critico`
        : `${API_URL}/programa/${this.programaSeleccionado.id}/decision-arrastre`;
      const body = {
        decision: this.form.decision,
        descripcion: this.form.descripcion || undefined,
      };
      if (this.form.decision === 'INICIAR_MODULO_0') {
        body.fecha_inicio = this.form.fecha_inicio;
      }
      try {
        await xaxios.post(endpoint, body);
        this.dialogDecision = false;
        if (this.form.decision === 'INICIAR_MODULO_0') {
          this.notificar(`Módulo 0 programado para el ${this.form.fecha_inicio}. El programa permanece en arrastre hasta que el sistema lo detecte con inscritos suficientes.`, 'success');
        } else if (this.form.decision === 'BAJA') {
          this.notificar('Programa dado de baja. Las inscripciones activas pasaron a RETIRADO.', 'success');
        } else {
          this.notificar('Decisión registrada: el programa continuará en desarrollo sin punto de equilibrio.', 'success');
        }
        await this.cargarTodo();
      } catch (err) {
        const msg = err?.response?.data?.error || err?.response?.data?.message || 'Error al registrar la decisión';
        const status = err?.response?.status;
        if (status === 400 && msg.includes('ya alcanzo el punto de equilibrio')) {
          this.notificar(`${msg} — Usa "Iniciar Módulo 0" en su lugar.`, 'warning');
        } else if (status === 400 && (msg.includes('no esta en Arrastre') || msg.includes('no esta en Arrastre Critico'))) {
          this.notificar('Este programa ya fue procesado por otro usuario. Actualizando bandeja...', 'warning');
          this.dialogDecision = false;
          await this.cargarTodo();
        } else {
          this.notificar(msg, 'error');
        }
      } finally {
        this.loadingDecision = false;
      }
    },

    notificar(msg, color = 'success') {
      this.snackbar = { show: true, msg, color };
    },
  },
};
</script>
